import { Loader } from '@googlemaps/js-api-loader';

import Component from '../../components/component/component';
import Helpers from '../../components/helpers/helpers';

import './map.scss';
import './import/img/bus.svg';
import './import/img/harbour.svg';
import './import/img/sailboat.svg';
import './import/img/train.svg';
import './import/img/tram.svg';
import './import/img/volta-pin.svg';

export interface IMapSettings {
    canvasSelector: string;
    inputSelector: string;
    lat: string;
    lng: string;
    markerSelector: string;
    primarySelector: string;
}

export interface IMapCategory {
    label: string;
}

export interface IMapPopup extends Partial<google.maps.OverlayView> {
    position?: google.maps.LatLng;
    new(position: google.maps.LatLng, content: HTMLElement);
}

let popup: IMapPopup;

/** Defines the Popup class. */
const definePopupClass: () => void = (): void => {
    class PopupPrototype extends google.maps.OverlayView {
        position: google.maps.LatLng;
        anchor: HTMLElement;
        timeout: number;

        constructor(position: google.maps.LatLng, content: HTMLElement) {
            super();

            this.position = position;
            const clone: HTMLElement = content.cloneNode(true) as HTMLElement;

            clone.classList.remove('is-disabled');
            clone.classList.add('popup-bubble-content');

            const pixelOffset: HTMLElement = document.createElement('div');

            pixelOffset.classList.add('popup-bubble-anchor');
            pixelOffset.appendChild(clone);

            this.anchor = document.createElement('div');
            this.anchor.classList.add('popup-tip-anchor');
            this.anchor.appendChild(pixelOffset);

            // Optionally stop clicks, etc., from bubbling up to the map.
            this.stopEventPropagation();
        }

        /** Called when the popup is added to the map. */
        onAdd(): void {
            this.getPanes().floatPane.appendChild(this.anchor);
        }

        /** Called when the popup is removed from the map. */
        onRemove(): void {
            if (this.anchor.parentElement) {
                this.anchor.parentElement.removeChild(this.anchor);
            }
        }

        /** Called when the popup needs to draw itself. */
        draw(): void {
            const divPosition: google.maps.Point = this.getProjection().fromLatLngToDivPixel(this.position);
            // Hide the popup when it is far out of view.
            const display: string = Math.abs(divPosition.x) < 4000 && Math.abs(divPosition.y) < 4000 ?
                'block' :
                'none';

            if (display === 'block') {
                this.anchor.style.left = divPosition.x + 'px';
                this.anchor.style.top = divPosition.y + 'px';
            }

            if (this.anchor.style.display !== display) {
                this.anchor.style.display = display;
            }

            $(this.anchor).on('mouseover touchstart mouseout touchend', (event: JQuery.MouseOverEvent | JQuery.MouseOutEvent | JQuery.TouchStartEvent | JQuery.TouchEndEvent) => {
                if (this.timeout) {
                    window.clearTimeout(this.timeout);
                }

                switch (event.type) {
                    case 'touchstart':
                    case 'mouseover':
                        if ($(event.currentTarget).hasClass('is-closing')) {
                            this.timeout = window.setTimeout(() => {
                                $(event.currentTarget).addClass('is-opening').removeClass('is-closing');
                            }, 11);
                        } else {
                            $(event.currentTarget).addClass('is-opening').removeClass('is-closing');
                        }
                        break;

                    case 'touchend':
                    case 'mouseout':
                        $(event.currentTarget).removeClass('is-opening').addClass('is-closing');

                        this.timeout = window.setTimeout(() => {
                            $(event.currentTarget).removeClass('is-closing');
                        }, 500);
                        break;
                }
            });
        }

        /** Stops clicks/drags from bubbling up to the map. */
        stopEventPropagation: () => void = (): void => {
            const anchor: HTMLElement = this.anchor;

            anchor.style.cursor = 'auto';

            ['click', 'dblclick', 'contextmenu', 'wheel', 'mousedown', 'touchstart',
                'pointerdown']
                .forEach((event: string) => {
                    anchor.addEventListener(event, (e: Event) => {
                        e.stopPropagation();
                    });
                });
        };
    }

    popup = PopupPrototype;
};

export default class Map extends Component {
    static initSelector: string = '.js-map';

    public activeCategory: IMapCategory;
    public canvas: HTMLElement;
    public googleMap: google.maps.Map;
    public initialChange: boolean = true;
    public latLng: google.maps.LatLng;
    public marker: google.maps.Marker;
    public markers: IMapPopup[];
    public overlay: google.maps.OverlayView | null = null;
    public popups: IMapPopup[];
    public projection: google.maps.MapCanvasProjection | null = null;
    public settings: IMapSettings;
    public styles: google.maps.MapTypeStyle[];

    constructor(target: HTMLElement) {
        super(target);

        this.settings = $.extend({
            canvasSelector: '.map__canvas',
            inputSelector: '.map__input',
            lat: null,
            lng: null,
            markerSelector: '.map__marker',
            primarySelector: '.map__marker--primary',
        }, this.element.data());

        this.activeCategory = {
            label: this.defaultGroupLabel,
        };
        this.canvas = this.element.find(this.settings.canvasSelector).get(0);
        this.markers = [];
        this.popups = [];
        this.styles = [
            {
                elementType: 'labels.text.fill',
                featureType: 'administrative',
                stylers: [
                    {
                        color: '#bdbaae',
                    },
                ],
            },
            {
                elementType: 'all',
                featureType: 'landscape',
                stylers: [
                    {
                        color: '#f2f2f2',
                    },
                ],
            },
            {
                elementType: 'geometry.fill',
                featureType: 'landscape',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#f3f2ee',
                    },
                ],
            },
            {
                elementType: 'all',
                featureType: 'poi',
                stylers: [
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                elementType: 'geometry.fill',
                featureType: 'poi',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#e0ead6',
                    },
                ],
            },
            {
                elementType: 'geometry.fill',
                featureType: 'poi.park',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#e6ecde',
                    },
                ],
            },
            {
                elementType: 'all',
                featureType: 'road',
                stylers: [
                    {
                        saturation: -100,
                    },
                    {
                        lightness: 45,
                    },
                ],
            },
            {
                elementType: 'geometry.fill',
                featureType: 'road',
                stylers: [
                    {
                        color: '#ebe9e1',
                    },
                ],
            },
            {
                elementType: 'labels.text.fill',
                featureType: 'road',
                stylers: [
                    {
                        color: '#77766a',
                    },
                ],
            },
            {
                elementType: 'all',
                featureType: 'road.highway',
                stylers: [
                    {
                        visibility: 'simplified',
                    },
                ],
            },
            {
                elementType: 'labels.icon',
                featureType: 'road.arterial',
                stylers: [
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                elementType: 'all',
                featureType: 'transit',
                stylers: [
                    {
                        visibility: 'off',
                    },
                ],
            },
            {
                elementType: 'all',
                featureType: 'water',
                stylers: [
                    {
                        color: '#d4e4eb',
                    },
                    {
                        visibility: 'on',
                    },
                ],
            },
            {
                elementType: 'geometry.fill',
                featureType: 'water',
                stylers: [
                    {
                        visibility: 'on',
                    },
                    {
                        color: '#e2e7e9',
                    },
                ],
            },
            {
                elementType: 'labels.text.fill',
                featureType: 'water',
                stylers: [
                    {
                        color: '#9b7f7f',
                    },
                ],
            },
            {
                elementType: 'labels.text.stroke',
                featureType: 'water',
                stylers: [
                    {
                        color: '#fef7f7',
                    },
                ],
            },
        ];

        if (window.gotoAndPlay.googleMapsApiKey) {
            const loader: Loader = new Loader({
                apiKey: window.gotoAndPlay.googleMapsApiKey,
                libraries: ['places'],
            });

            loader.load().then((): void => {
                this.latLng = new google.maps.LatLng(parseFloat(this.settings.lat), parseFloat(this.settings.lng));
                this.init();
            });
        } else {
            console.error('No Google Maps API key defined in gotoAndPlay');
        }
    }

    init(): void {
        definePopupClass();

        this.createMap();
        this.loadMarkers(this.activeCategory);
        this.setCategoryEvents();
    }

    get defaultGroupLabel(): string {
        return this.element.find(this.settings.inputSelector +':checked').eq(0).parent().data('category');
    }

    createMap(): void {
        this.googleMap = new google.maps.Map(this.canvas, {
            center: this.latLng,
            disableDefaultUI: true,
            draggable: true,
            gestureHandling: 'cooperative',
            scrollwheel: false,
            styles: this.styles,
            zoom: window.matchMedia('(min-width: ' + Helpers.bp.sm + 'px)').matches ? 15 : 14,
            zoomControl: true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.TOP_RIGHT,
            },
        });

        this.overlay = new google.maps.OverlayView();
        this.overlay.setMap(this.googleMap);

        google.maps.event.addListener(this.googleMap, 'idle', () => {
            this.projection = this.overlay.getProjection();
        });
    }

    loadMarkers(category: IMapCategory): void {
        const els: NodeListOf<HTMLElement> = document.querySelectorAll(this.settings.markerSelector + '[data-category="' + category.label + '"]');

        [].forEach.call(els, this.renderMarker);
    }

    renderMarker: (el: HTMLElement) => void = (el: HTMLElement): void => {
        const p: IMapPopup = new popup(
            this.getLatLng(el),
            el);

        p.setMap(this.googleMap);

        this.markers.push(p);
        this.popups.push(p);
    };

    getLatLng: (el: HTMLElement) => google.maps.LatLng = (el: HTMLElement): google.maps.LatLng => new google.maps.LatLng(parseFloat(el.getAttribute('data-lat')), parseFloat(el.getAttribute('data-lng')));

    fitBounds(markers: IMapPopup[]): void {
        const bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds();

        markers.forEach((element: IMapPopup) => {
            bounds.extend(element.position);
        });

        this.googleMap.fitBounds(bounds);
        this.googleMap.setZoom(this.googleMap.getZoom());
    }

    setCategoryEvents(): void {
        const els: NodeListOf<HTMLElement> = document.querySelectorAll(this.settings.inputSelector);
        let checkedElement: HTMLInputElement | null = null;

        [].forEach.call(els, (el: HTMLInputElement) => {
            if (el.checked) {
                checkedElement = el;
            }

            el.addEventListener('change', (e: Event) => {
                const target: HTMLElement = e.currentTarget as HTMLElement;
                const data: DOMStringMap = target.parentElement.dataset;

                this.handleCategoryChange(data);
            });
        });

        this.activateFirst(checkedElement ? checkedElement : els.item(0));
    }

    activateFirst(element: HTMLElement): void {
        const data: DOMStringMap = element.parentElement.dataset;

        this.handleCategoryChange(data);
    }

    handleCategoryChange: (data: DOMStringMap) => void = (data: DOMStringMap): void => {
        const mapedData: { [ key in string]: string } = JSON.parse(JSON.stringify(data)) as { [ key in string]: string };

        const category: IMapCategory = {
            label: mapedData.category,
        };

        this.activeCategory = category;

        this.resetPopups();

        this.markers = [];
        this.setPrimaryMarker();
        this.loadMarkers(category);

        if (this.initialChange) {
            this.initialChange = false;
        } else {
            this.fitBounds(this.markers);
        }

        this.loadMarkers({
            label: 'default',
        });
    };

    resetPopups: () => void = (): void => {
        this.popups.forEach((p: IMapPopup) => p.setMap(null));
        this.popups = [];
    };

    setPrimaryMarker(): void {
        const el: HTMLElement = document.querySelector(this.settings.primarySelector);

        this.renderMarker(el);
    }
}
